<template>
  <aside
    class="conversation-list border rounded bg-body-emphasis"
    aria-label="Conversations"
  >
    <div class="d-flex justify-content-between gap-2 border-bottom p-3 small">
      <strong>{{ ready ? `${total} conversations` : "Conversations" }}</strong
      ><span class="text-muted">Newest first</span>
    </div>
    <p v-if="loading" class="p-3 mb-0" role="status">Loading conversations…</p>
    <div v-else-if="failed" class="p-3" role="alert">
      Could not load conversations.
      <button class="btn btn-link btn-sm" @click="$emit('retry')">
        Try again
      </button>
    </div>
    <p v-else-if="ready && !rows.length" class="p-3 text-muted mb-0">
      No conversations match these filters.
    </p>
    <template v-for="group in groups" :key="group.day">
      <h6 class="day-label px-3 py-2 mb-0 bg-body text-muted">
        {{ group.day }}
      </h6>
      <button
        v-for="row in group.rows"
        :key="row.id"
        class="row-button"
        :class="{ selected: row.id === selectedId }"
        :aria-current="row.id === selectedId ? 'true' : undefined"
        @click="$emit('select', row)"
      >
        <div
          class="d-flex align-items-center justify-content-between gap-2 mb-1"
        >
          <strong class="small">{{
            conversationTypeLabel(row.conversation_type)
          }}</strong
          ><span class="badge" :class="outcomePill(row.outcome).className">{{
            outcomePill(row.outcome).label
          }}</span>
        </div>
        <div class="text-truncate text-muted small">
          {{ mode === "account" ? row.company.name : row.account.email }}
        </div>
        <div
          class="d-flex flex-wrap align-items-center gap-1 text-muted small mt-1"
        >
          <span
            >{{
              new Date(row.created_at).toLocaleTimeString([], {
                hour: "2-digit",
                minute: "2-digit",
              })
            }}
            ·
            {{
              row.inferred_turn_count
                ? `${row.inferred_turn_count} inferred turns`
                : "0 turns, nothing logged"
            }}
            · {{ row.upload_count }} files</span
          >
          <span
            v-if="row.account.is_staff"
            class="badge bg-secondary-subtle text-secondary-emphasis"
            >Soldera</span
          ><span
            v-if="row.deleted || row.company.deleted"
            class="badge bg-secondary-subtle text-secondary-emphasis"
            >Archived</span
          ><span class="ms-auto">#{{ row.id }}</span>
        </div>
      </button>
    </template>
    <p v-if="moreFailed" class="small text-danger px-3 mt-2 mb-0" role="alert">
      Could not load older conversations. Try again.
    </p>
    <button
      v-if="hasMore"
      class="btn btn-link w-100 border-top"
      :disabled="loadingMore"
      @click="$emit('more')"
    >
      {{ loadingMore ? "Loading…" : "Show older conversations" }}
    </button>
  </aside>
</template>
<script setup lang="ts">
import { computed } from "vue";
import type { AiConversationRow } from "./aiConversationsTypes";
import {
  conversationTypeLabel,
  outcomePill,
} from "./aiConversationsPresenters";
const props = defineProps<{
  rows: AiConversationRow[];
  total: number;
  selectedId: number | null;
  mode: "company" | "account";
  hasMore: boolean;
  loading: boolean;
  ready: boolean;
  failed: boolean;
  loadingMore: boolean;
  moreFailed: boolean;
}>();
defineEmits<{
  select: [row: AiConversationRow];
  more: [];
  retry: [];
}>();
const groups = computed(() =>
  Object.entries(
    Object.groupBy(props.rows, (row) =>
      new Date(row.created_at).toLocaleDateString(undefined, {
        weekday: "short",
        day: "numeric",
        month: "short",
        year: "numeric",
      })
    )
  ).map(([day, rows]) => ({ day, rows: rows ?? [] }))
);
</script>
<style scoped lang="scss">
.conversation-list {
  overflow: hidden;
}
.day-label {
  font-size: 0.7rem;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}
.row-button {
  display: block;
  width: 100%;
  text-align: left;
  background: transparent;
  color: inherit;
  border: 0;
  border-bottom: 1px solid var(--phoenix-border-color);
  padding: 0.8rem 1rem;
}
.row-button:hover,
.row-button.selected {
  background: var(--phoenix-primary-bg-subtle, #eef5f1);
}
.row-button.selected {
  box-shadow: inset 3px 0 var(--phoenix-primary, #1f4d33);
}
</style>
